import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, CircularProgress, CssBaseline, ThemeProvider, Typography,
} from '@mui/material';
import publicWineListService from '../../services/publicWineListService';
import type { WineListRender } from '../../types/venue';
import { previewTheme, WineListMenuView } from './PreviewMock';

/**
 * Public, customer-facing wine list at /m/:slug.
 *
 * Reuses the WineListMenuView component used by /m/preview-mock — same dark
 * restaurant theme, same typography, same interactions. The only differences
 * are data fetching and the loading / error states below.
 *
 * Accessed by customers via QR code; no authentication.
 */
const PublicWineList: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<WineListRender | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * 'not-found'  → share missing, archived, or wine list not ACTIVE
   * 'error'      → network / server error
   * null         → no error
   */
  const [error, setError] = useState<'not-found' | 'error' | null>(null);

  useEffect(() => {
    if (!slug) {
      setError('not-found');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    publicWineListService.getBySlug(slug)
      .then(setData)
      .catch(err => {
        if (err?.response?.status === 404) setError('not-found');
        else setError('error');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <ThemeProvider theme={previewTheme}>
      <CssBaseline />
      {loading && <CenteredState><CircularProgress size={32} /></CenteredState>}
      {!loading && error === 'not-found' && (
        <CenteredState>
          <Typography
            sx={{
              fontSize: '0.7rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'primary.main',
              mb: 1.5,
              fontWeight: 600,
            }}
          >
            Wine list unavailable
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Cormorant Garamond", "EB Garamond", Georgia, serif',
              fontSize: '1.4rem',
              color: 'text.primary',
              textAlign: 'center',
              maxWidth: 360,
              lineHeight: 1.3,
            }}
          >
            This list isn't available right now. It may have been unpublished by the venue.
          </Typography>
        </CenteredState>
      )}
      {!loading && error === 'error' && (
        <CenteredState>
          <Typography sx={{ color: 'text.secondary' }}>
            Couldn't load this wine list. Please try again.
          </Typography>
        </CenteredState>
      )}
      {!loading && !error && data && <WineListMenuView data={data} />}
    </ThemeProvider>
  );
};

const CenteredState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      color: 'text.primary',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
    }}
  >
    {children}
  </Box>
);

export default PublicWineList;
